import { ChevronLeft, ChevronRight } from "components/icons/generated";
import Box from "ds/components/Box";
import ButtonIcon from "ds/components/ButtonIcon";
import TileWrapper from "ds/components/Tile/Wrapper";
import Typography from "ds/components/Typography";
import useTypedContext from "hooks/useTypedContext";
import { BillingTier } from "types/generated";

import BillingUpgradeAvailableBox from "../components/UpgradeAvailableBox";
import { BillingContext } from "../Context";
import { isSelfServiceTier } from "../utils";
import { getUpgradeTiers } from "./utils";
import BillingUpgradeOptionsCard from "./Card";
import BillingUpgradeOptionsCardHorizontal from "./Card/Horizontal";
import BillingUpgradeOptionsCarousel from "./Carousel";
import useBillingUpgradeOptionsCarouselScroll from "./Carousel/useScroll";
import { BASE_INFO_BY_TIER, UPGRADE_BOX_BY_TIER } from "./constants";
import BillingUpgradeOptionsStarterPlanCard from "./StarterPlanCard";

const renderUpgradeBox = (tier: BillingTier) => {
  const box = UPGRADE_BOX_BY_TIER[tier];

  if (!box) {
    return undefined;
  }

  return <BillingUpgradeAvailableBox title={box.title} items={box.items} />;
};

const renderCard = (cardTier: BillingTier) => {
  if (isSelfServiceTier(cardTier)) {
    return <BillingUpgradeOptionsStarterPlanCard key={cardTier} tier={cardTier} />;
  }

  return (
    <BillingUpgradeOptionsCard
      key={cardTier}
      tier={cardTier}
      title={BASE_INFO_BY_TIER[cardTier]?.title || ""}
      price={BASE_INFO_BY_TIER[cardTier]?.price || ""}
      description={BASE_INFO_BY_TIER[cardTier]?.description || ""}
      upgradeBox={renderUpgradeBox(cardTier)}
    />
  );
};

const BillingUpgradeOptions = () => {
  const { isTrial, tier } = useTypedContext(BillingContext);
  const { trackRef, atStart, atEnd, scroll, updateBounds } =
    useBillingUpgradeOptionsCarouselScroll();

  const upgradeTiers = getUpgradeTiers(isTrial ? BillingTier.V5Free : tier);

  if (upgradeTiers.length === 0) {
    return null;
  }

  const isSingleCard = upgradeTiers.length === 1;
  const isScrollable = !atStart || !atEnd;

  return (
    <TileWrapper padding="large x-large x-large x-large" gap="large" direction="column" grow="0">
      <Box justify="between" align="center" gap="large" fullWidth>
        <Typography tag="h3" variant="p-t6">
          Upgrade options
        </Typography>

        {!isSingleCard && isScrollable && (
          <Box gap="medium">
            <ButtonIcon
              icon={ChevronLeft}
              variant="secondary"
              disabled={atStart}
              onPress={() => scroll(-1)}
            >
              Scroll left
            </ButtonIcon>
            <ButtonIcon
              icon={ChevronRight}
              variant="secondary"
              disabled={atEnd}
              onPress={() => scroll(1)}
            >
              Scroll right
            </ButtonIcon>
          </Box>
        )}
      </Box>

      {isSingleCard && (
        <BillingUpgradeOptionsCardHorizontal
          tier={upgradeTiers[0]}
          title={BASE_INFO_BY_TIER[upgradeTiers[0]]?.title || ""}
          price={BASE_INFO_BY_TIER[upgradeTiers[0]]?.price || ""}
          description={BASE_INFO_BY_TIER[upgradeTiers[0]]?.description || ""}
          upgradeBox={renderUpgradeBox(upgradeTiers[0])}
        />
      )}

      {!isSingleCard && (
        <BillingUpgradeOptionsCarousel
          trackRef={trackRef}
          onScroll={updateBounds}
          atStart={atStart}
          atEnd={atEnd}
        >
          {upgradeTiers.map(renderCard)}
        </BillingUpgradeOptionsCarousel>
      )}
    </TileWrapper>
  );
};

export default BillingUpgradeOptions;
